/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    X, ArrowRight, Building2, User, ShieldCheck, Star, Award,
    ClipboardList, Users, Sparkles, MessageSquare, Lock, Briefcase,
} from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const AGENCES_CONTENT = {
    icon: Building2,
    accent: '#FF914D',
    accentSoft: 'rgba(255, 145, 77, 0.12)',
    badge: 'Pour les agences et groupes',
    h1: 'Pilotez la qualité.',
    h1Span: 'Construisez votre réputation collective.',
    pitch:
        "Homly structure le suivi vendeur, valorise le travail des équipes et renforce la confiance accordée à votre marque. L'agence reste au centre — données, dossiers, historiques et avis collectifs vous appartiennent.",
    benefits: [
        { icon: ClipboardList, text: 'Centralisez le suivi de chaque mandat.' },
        { icon: Star, text: 'Recueillez des avis vendeurs vérifiés au nom de l\'agence.' },
        { icon: Users, text: 'Valorisez chacun de vos collaborateurs.' },
        { icon: Lock, text: 'Conservez la propriété de vos données et historiques.' },
    ],
    pageHref: '/pour-les-agences',
    pageCta: 'Voir le pitch complet',
};

const AGENTS_CONTENT = {
    icon: User,
    accent: '#C7A1F0',
    accentSoft: 'rgba(168, 85, 247, 0.15)',
    badge: 'Pour les agents immobiliers',
    h1: 'Votre travail réel,',
    h1Span: 'transformé en réputation publique.',
    pitch:
        "Homly transforme la qualité de vos accompagnements en preuves visibles : avis vendeurs vérifiés, spécialités, expérience et profil professionnel. Votre identité pro vous accompagne dans votre carrière, les données vendeurs restent à l'agence.",
    benefits: [
        { icon: Star, text: 'Avis vendeurs vérifiés sur des dossiers réels.' },
        { icon: Award, text: 'Profil pro vérifié, spécialités mises en avant.' },
        { icon: Sparkles, text: 'Visibilité locale et demandes qualifiées.' },
        { icon: Briefcase, text: 'Inclus avec votre agence, enrichissable en option.' },
    ],
    pageHref: '/pour-les-agents',
    pageCta: 'Voir le pitch complet',
};

/**
 * PersonaModal — Affiche le pitch dédié à un persona (agences ou agents)
 * sans quitter la landing. Garde le visiteur dans le funnel.
 *
 * Props :
 *  - persona       : 'agences' | 'agents'
 *  - isOpen        : bool
 *  - onClose       : () => void
 *  - onDemoClick   : () => void   (ouvre le DemoRequestModal)
 */
export const PersonaModal = ({ persona, isOpen, onClose, onDemoClick }) => {
    const content = persona === 'agents' ? AGENTS_CONTENT : AGENCES_CONTENT;
    const Icon = content.icon;

    const handleDemoClick = () => {
        onClose();
        onDemoClick && onDemoClick();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md"
                    onClick={onClose}
                    data-testid={`persona-modal-${persona}`}
                >
                    <motion.div
                        initial={{ scale: 0.95, y: 20, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.95, y: 20, opacity: 0 }}
                        transition={{ duration: 0.28, ease: [0.21, 0.5, 0.26, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl border border-white/12 bg-[#0E0918] shadow-2xl shadow-black/60"
                    >
                        {/* Halo subtil */}
                        <div
                            className="pointer-events-none absolute -top-32 -right-32 w-72 h-72 rounded-full blur-[120px]"
                            style={{ backgroundColor: content.accentSoft }}
                        />

                        {/* Header */}
                        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/8 bg-[#0E0918]/95 backdrop-blur">
                            <span
                                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-medium tracking-widest uppercase"
                                style={{
                                    backgroundColor: content.accentSoft,
                                    color: content.accent,
                                    fontFamily: "'JetBrains Mono', monospace",
                                    border: `1px solid ${content.accent}40`,
                                }}
                            >
                                <Icon className="w-3 h-3" />
                                {content.badge}
                            </span>
                            <button
                                onClick={onClose}
                                aria-label="Fermer"
                                data-testid="persona-modal-close"
                                className="w-9 h-9 rounded-full flex items-center justify-center text-[#A19DAB] hover:text-white hover:bg-white/5 transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="relative px-6 md:px-8 py-6 md:py-8">
                            <h2
                                className="text-3xl sm:text-4xl font-bold tracking-tighter text-white leading-[1.1]"
                                style={DISPLAY}
                            >
                                {content.h1}{' '}
                                <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                                    {content.h1Span}
                                </span>
                            </h2>
                            <p className="mt-4 text-sm md:text-base text-[#A19DAB] leading-relaxed">
                                {content.pitch}
                            </p>

                            {/* Bénéfices */}
                            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="persona-modal-benefits">
                                {content.benefits.map(({ icon: BI, text }, i) => (
                                    <motion.li
                                        key={text}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.35, delay: 0.1 + i * 0.06 }}
                                        className="flex items-start gap-3 rounded-2xl border border-white/8 bg-white/[0.02] p-3 md:p-4"
                                    >
                                        <span
                                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: content.accentSoft }}
                                        >
                                            <BI className="w-4 h-4" style={{ color: content.accent }} />
                                        </span>
                                        <span className="text-sm text-white/90 leading-snug">{text}</span>
                                    </motion.li>
                                ))}
                            </ul>

                            {/* Réassurance courte */}
                            <p className="mt-5 flex items-center gap-2 text-xs text-[#A19DAB]" style={MONO}>
                                <ShieldCheck className="w-3.5 h-3.5 text-[#A855F7]" />
                                Sans engagement · Démo personnalisée · 20 minutes
                            </p>
                        </div>

                        {/* Footer actions */}
                        <div className="sticky bottom-0 flex flex-col sm:flex-row gap-3 px-6 md:px-8 py-4 border-t border-white/8 bg-[#0E0918]/95 backdrop-blur">
                            <button
                                onClick={handleDemoClick}
                                data-testid="persona-modal-demo-cta"
                                className="group flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-semibold text-sm shadow-xl shadow-[#681793]/30 hover:opacity-95 hover:scale-[1.01] transition-all"
                            >
                                Demander une démonstration
                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                            </button>
                            <Link
                                to={content.pageHref}
                                onClick={onClose}
                                data-testid="persona-modal-full-page"
                                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/15 hover:border-white/30 text-white/90 hover:text-white font-medium text-sm transition-all"
                            >
                                <MessageSquare className="w-4 h-4" />
                                {content.pageCta}
                            </Link>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
